import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  MenuItem, 
  Alert,
  Divider
} from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import { getContent } from '../utils/contentLoader';
import { getActivePractices } from '../services/patientService';
import { saveRecordsReleaseAuth, sendRecordsReleaseEmail } from '../services/recordsReleaseService';
import useDobConfirm from '../hooks/useDobConfirm';
import DobConfirmDialog from './DobConfirmDialog';

export default function MedicalRecordsRelease() {
  const { t, i18n } = useTranslation('medicalRecordsRelease');
  
  // Load language-aware content
  const medicalRecordsReleaseContent = useMemo(() => getContent('medicalRecordsRelease', i18n.language), [i18n.language]);
  // Patient identification fields
  const [practice, setPractice] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientId, setPatientId] = useState(''); // PID
  const [dob, setDobRaw] = useState('');
  const [gender, setGender] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState('');
  
  // Practice loading
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);
  const [practicesError, setPracticesError] = useState(null);
  
  // DOB confirmation
  const { dobInputRef, showDobConfirmDialog, handleDobBlur, handleDobChange: setDob, confirmDob, fixDob } = useDobConfirm(dob, setDobRaw);

  // Authorization specific fields
  const [recipient, setRecipient] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [relationship, setRelationship] = useState('');
  const [recordsType, setRecordsType] = useState('all');
  const [recordsDetails, setRecordsDetails] = useState('');
  const [purpose, setPurpose] = useState('');
  const [authStartDate, setAuthStartDate] = useState('');
  const [authEndDate, setAuthEndDate] = useState('');
  
  // Signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  
  // Submission state
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load practices on mount
  useEffect(() => {
    const loadPractices = async () => {
      try {
        setPracticesLoading(true);
        const activePractices = await getActivePractices();
        setPractices(activePractices);
        setPracticesError(null);
      } catch (error) {
        console.error('Failed to load practices:', error);
        setPracticesError('Failed to load practices');
        setPractices([
          { practice_name: 'STARS Plastic Surgery' },
          { practice_name: 'San Antonio General Surgery' }
        ]);
      } finally {
        setPracticesLoading(false);
      }
    };
    loadPractices();

    // Pre-populate fields from URL parameters if present
    const urlParams = new URLSearchParams(window.location.search);
    const practiceParam = urlParams.get('practice');
    const nameParam = urlParams.get('name');
    const pidParam = urlParams.get('pid'); // Patient ID
    const dobParam = urlParams.get('dob');
    const genderParam = urlParams.get('gender');
    const phoneParam = urlParams.get('phone');
    const emailParam = urlParams.get('email');
    
    // Authorization fields
    const recipientParam = urlParams.get('recipient');
    const recipientAddressParam = urlParams.get('recipient_address');
    const relationshipParam = urlParams.get('relationship');
    const recordsTypeParam = urlParams.get('records_type');
    const recordsDetailsParam = urlParams.get('records_details');
    const purposeParam = urlParams.get('purpose');

    if (practiceParam) setPractice(practiceParam);
    if (nameParam) setPatientName(nameParam);
    if (pidParam) setPatientId(pidParam); // Pre-fill PID from URL
    if (dobParam) setDob(dobParam);
    if (genderParam) setGender(genderParam);
    if (phoneParam) setCellPhone(phoneParam);
    if (emailParam) setEmail(emailParam);
    
    // Set authorization fields
    if (recipientParam) setRecipient(recipientParam);
    if (recipientAddressParam) setRecipientAddress(recipientAddressParam);
    if (relationshipParam) setRelationship(relationshipParam);
    if (recordsTypeParam) setRecordsType(recordsTypeParam);
    if (recordsDetailsParam) setRecordsDetails(recordsDetailsParam);
    if (purposeParam) setPurpose(purposeParam);
  }, []);

  // Prefill signature date with today if empty
  useEffect(() => {
    if (!date) {
      const today = new Date();
      const iso = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
        .toISOString()
        .slice(0, 10);
      setDate(iso);
    }
  }, [date]);

  // Set default authorization dates: start = today, end = one year from today
  useEffect(() => {
    if (!authStartDate) {
      const today = new Date();
      const iso = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
        .toISOString()
        .slice(0, 10);
      setAuthStartDate(iso);
    }
    if (!authEndDate) {
      const today = new Date();
      const oneYearLater = new Date(Date.UTC(today.getFullYear() + 1, today.getMonth(), today.getDate()));
      const iso = oneYearLater.toISOString().slice(0, 10);
      setAuthEndDate(iso);
    }
  }, [authStartDate, authEndDate]);

  // Derive practiceCode when practice selection changes
  useEffect(() => {
    if (!practice) {
      setPracticeCode('');
      return;
    }
    const match = (practices || []).find((p) => p.practice_name === practice);
    setPracticeCode(match?.practice_code || '');
  }, [practice, practices]);

  // Phone formatting helpers
  const onlyDigits = (s) => (s || '').replace(/\D/g, '');
  const formatPhone = (digits) => {
    const d = onlyDigits(digits).slice(0, 10);
    if (!d) return '';
    return d.replace(/^(\d{3})(\d{0,3})(\d{0,4})$/, (m, a, b, c) => {
      let out = a;
      if (b) out += '-' + b;
      if (c) out += '-' + c;
      return out;
    });
  };
  
  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    setCellPhone(cleaned);
  };
  
  const isValidPhone = cellPhone && cellPhone.length === 10;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(email);

  // Validation
  const idComplete = Boolean(
    practice && patientName && dob && gender && isValidPhone && isValidEmail
  );
  const authComplete = Boolean(
    recipient && recipient.trim() !== '' && 
    relationship && relationship.trim() !== '' &&
    recordsType &&
    (recordsType === 'all' || (recordsDetails && recordsDetails.trim() !== '')) &&
    authStartDate &&
    authEndDate
  );
  const hasSignature = Boolean(signatureDataUrl);
  const canSubmit = idComplete && authComplete && hasSignature;

  // Signature canvas handlers
  const handleMouseDown = (e) => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureDataUrl(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSignatureDataUrl('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Please complete all required fields and sign the authorization.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        practiceName: practice,
        practiceCode: practiceCode || null,
        patientName,
        patientId: patientId || null, // PID
        dob,
        gender,
        cellPhone: formatPhone(cellPhone),
        email,
        recipient,
        recipientAddress: recipientAddress || null,
        relationship,
        recordsType,
        recordsDetails: recordsDetails || null,
        purpose: purpose || null,
        authorizationStart: authStartDate,
        authorizationEnd: authEndDate,
        signatureDataUrl,
        dateSigned: date,
        timestamp: new Date().toISOString()
      };

      console.log('Submitting medical records release authorization:', payload);

      // Save authorization to database and upload to S3
      const result = await saveRecordsReleaseAuth(payload);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save authorization');
      }

      console.log('✅ Authorization saved successfully with ID:', result.id);

      // Send confirmation email with PDF attachment
      try {
        const emailResult = await sendRecordsReleaseEmail(payload);
        if (emailResult.success) {
          console.log('✅ Confirmation email sent successfully');
        } else {
          console.warn('⚠️ Email sending failed:', emailResult.error);
          // Don't fail the whole submission if email fails
        }
      } catch (emailErr) {
        console.error('Email sending error:', emailErr);
        // Continue even if email fails
      }

      setSubmitSuccess(true);
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit authorization. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {submitSuccess ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('success.title')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('success.thankYou', { practice })}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('success.emailSent', { email })}
            </Typography>
            <Typography variant="body2">
              {t('success.saved')}
            </Typography>
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{t('success.summaryTitle')}</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>{t('success.recipient')}:</strong> {recipient}<br />
              • <strong>{t('success.recordsType')}:</strong> {recordsType === 'all' ? t('success.allRecords') : recordsType}<br />
              • <strong>{t('success.effectivePeriod')}:</strong> {authStartDate} to {authEndDate}
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              href="/"
            >
              {t('buttons.returnToHome')}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <LanguageSwitcher />
          </Box>
          <Typography variant="h4" align="center" gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom sx={{ mb: 3 }}>
            {t('subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Patient Identification Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('sections.patientIdentification')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t('labels.practice')}
                  value={practice}
                  onChange={(e) => setPractice(e.target.value)}
                  disabled={practicesLoading}
                  error={!!practicesError}
                  helperText={practicesError ? t('errors.fallbackList') : ''}
                >
                  {(practices || []).map((p) => (
                    <MenuItem key={p.practice_name} value={p.practice_name}>
                      {p.practice_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  required
                  label={t('labels.fullName')} 
                  value={patientName} 
                  onChange={(e) => setPatientName(e.target.value)} 
                />
              </Grid>
              {/* PID field hidden - populated via URL parameter only */}
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  required
                  type="date" 
                  label={t('labels.dateOfBirth')} 
                  InputLabelProps={{ shrink: true }} 
                  inputRef={dobInputRef}
                  value={dob} 
                  onChange={(e) => setDob(e.target.value)} 
                  onBlur={handleDobBlur}
                />
                <DobConfirmDialog open={showDobConfirmDialog} dob={dob} onFix={fixDob} onConfirm={confirmDob} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  select 
                  fullWidth 
                  required
                  label={t('labels.gender')} 
                  value={gender} 
                  onChange={(e) => setGender(e.target.value)}
                >
                  <MenuItem value="Male">{t('genderOptions.male')}</MenuItem>
                  <MenuItem value="Female">{t('genderOptions.female')}</MenuItem>
                  <MenuItem value="Other">{t('genderOptions.other')}</MenuItem>
                  <MenuItem value="Prefer not to say">{t('genderOptions.preferNotToSay')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('labels.cellPhone')}
                  value={formatPhone(cellPhone)}
                  onChange={handlePhoneChange}
                  placeholder={t('placeholders.phone')}
                  error={cellPhone && cellPhone.length !== 10}
                  helperText={cellPhone && cellPhone.length !== 10 ? t('errors.validPhone') : ''}
                  inputMode="tel"
                  inputProps={{ maxLength: 12 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label={t('labels.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('placeholders.email')}
                  error={email && !isValidEmail}
                  helperText={email && !isValidEmail ? t('errors.validEmail') : ''}
                  inputMode="email"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Authorization Content - Section 1 */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 3 }}>
              {medicalRecordsReleaseContent.section1.map((paragraph, idx) => (
                <Typography 
                  key={idx} 
                  variant={idx === 0 ? "h6" : "body2"} 
                  align={idx === 0 ? "center" : "justify"}
                  gutterBottom={idx === 0}
                  sx={{ mb: idx === 0 ? 2 : 1.5, whiteSpace: 'pre-wrap' }}
                >
                  {paragraph.replace(/\[Medical practice\]|\[Medical Practice\]/g, practice || '[Medical practice]')}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Authorization Fields */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('labels.authorizeDisclosure', { practice: practice || '[PRACTICE NAME]' })}
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder={t('placeholders.recipient')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('labels.recipientAddress')}
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.target.value)}
                  placeholder={t('placeholders.recipientAddress')}
                  helperText={t('helperTexts.recipientAddress')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('labels.relationship')}
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  placeholder={t('placeholders.relationship')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t('labels.recordsType')}
                  value={recordsType}
                  onChange={(e) => setRecordsType(e.target.value)}
                >
                  <MenuItem value="all">{t('recordsTypeOptions.all')}</MenuItem>
                  <MenuItem value="specific">{t('recordsTypeOptions.specific')}</MenuItem>
                  <MenuItem value="date_range">{t('recordsTypeOptions.dateRange')}</MenuItem>
                </TextField>
              </Grid>
              {recordsType !== 'all' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={2}
                    label={t('labels.specifyRecords')}
                    value={recordsDetails}
                    onChange={(e) => setRecordsDetails(e.target.value)}
                    placeholder={t('placeholders.specifyRecords')}
                    helperText={t('helperTexts.specifyRecords')}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('labels.purposeOfDisclosure')}
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder={t('placeholders.purpose')}
                  helperText={t('helperTexts.purpose')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label={t('labels.authStartDate')}
                  InputLabelProps={{ shrink: true }}
                  value={authStartDate}
                  onChange={(e) => setAuthStartDate(e.target.value)}
                  helperText={t('helperTexts.authStartDate')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label={t('labels.authEndDate')}
                  InputLabelProps={{ shrink: true }}
                  value={authEndDate}
                  onChange={(e) => setAuthEndDate(e.target.value)}
                  helperText={t('helperTexts.authEndDate')}
                  inputProps={{ min: authStartDate }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Authorization Content - Section 2 */}
            <Box>
              {medicalRecordsReleaseContent.section2.map((paragraph, idx) => (
                <Typography 
                  key={idx} 
                  variant="body2" 
                  align="justify"
                  sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Paper>

          {/* Signature Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('sections.signature')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('signatureSection.instructions')}
            </Typography>
            <Box
              sx={{
                border: '2px solid #ccc',
                borderRadius: 1,
                mt: 2,
                mb: 2,
                backgroundColor: '#fafafa',
                cursor: 'crosshair',
                touchAction: 'none',
              }}
            >
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                style={{ width: '100%', height: '180px', display: 'block' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={(e) => {
                  const touch = e.touches[0];
                  const canvas = e.currentTarget;
                  const rect = canvas.getBoundingClientRect();
                  const ctx = canvas.getContext('2d');
                  ctx.strokeStyle = '#000';
                  ctx.lineWidth = 2;
                  ctx.lineCap = 'round';
                  ctx.beginPath();
                  ctx.moveTo(
                    (touch.clientX - rect.left) * (canvas.width / rect.width),
                    (touch.clientY - rect.top) * (canvas.height / rect.height)
                  );
                  setIsDrawing(true);
                }}
                onTouchMove={(e) => {
                  if (!isDrawing) return;
                  const touch = e.touches[0];
                  const canvas = e.currentTarget;
                  const rect = canvas.getBoundingClientRect();
                  const ctx = canvas.getContext('2d');
                  ctx.lineTo(
                    (touch.clientX - rect.left) * (canvas.width / rect.width),
                    (touch.clientY - rect.top) * (canvas.height / rect.height)
                  );
                  ctx.stroke();
                }}
                onTouchEnd={handleMouseUp}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button variant="outlined" onClick={clearSignature}>
                {t('buttons.clearSignature')}
              </Button>
            </Box>
            <TextField
              fullWidth
              type="date"
              label={t('labels.date')}
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Paper>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              sx={{ minWidth: 200 }}
            >
              {isSubmitting ? t('buttons.submitting') : t('buttons.submitAuthorization')}
            </Button>
          </Box>

          {!canSubmit && (
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
              {t('errors.incompleteForm')}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
